import React, { useState, useEffect } from "react";
import { db } from "../config/firestore";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import HighScore from "./HighScore";
import EasterEgg from "./EasterEgg";

const AdventCalendar = ({ user, onDaySelect, userProgress, onLogin }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authKey, setAuthKey] = useState("");

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    onLogin(authKey);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.reload();
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const q = query(
        collection(db, "adventChallenges"),
        orderBy("day", "asc")
      );
      const querySnapshot = await getDocs(q);
      const challengeList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setChallenges(challengeList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching challenges:", error);
      setLoading(false);
    }
  };

  const getCurrentDay = () => {
    const now = new Date();
    if (now.getMonth() !== 11) return 0; // Not December
    const dayOfMonth = now.getDate();
    return dayOfMonth <= 24 ? dayOfMonth : 24;
  };

  const isDayUnlocked = (challenge) => {
    if (!challenge) return false;

    const unlockDay =
      challenge.unlockDay !== undefined ? challenge.unlockDay : challenge.day;
    const currentDay = getCurrentDay();
    return currentDay >= unlockDay;
  };

  const isDayCompleted = (day) => {
    return userProgress && userProgress[`day${day}`]?.completed;
  };

  const getDayScore = (day) => {
    return (userProgress && userProgress[`day${day}`]?.score) || 0;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-advent-primary-dark to-advent-secondary-dark">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white mb-4"></div>
        <div className="text-white text-2xl">Laster julekalender...</div>
      </div>
    );
  }

  // Show login screen if not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-advent-primary-dark via-advent-primary to-advent-secondary-dark text-white py-8 px-4">
        <div className="w-full max-w-md bg-white text-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-4xl font-bold text-center mb-2 text-advent-primary-dark">
            🎄 Mobilitets adventskalender 🎄
          </h1>
          <p className="text-center text-gray-600 mb-4">
            Gjett steder i Norge hver dag fram til julaften!
          </p>
        </div>

        <div className="w-full max-w-md bg-white text-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Logg inn</h2>
              <p className="text-sm text-gray-600">
                Bruk din invitasjonsnøkkel for å delta
              </p>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Invitasjonsnøkkel
              </span>
              <input
                type="text"
                value={authKey}
                onChange={(e) => setAuthKey(e.target.value)}
                className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring focus:ring-advent-primary-light focus:border-advent-primary-light"
                placeholder="Skriv inn nøkkelen din"
                required
              />
            </label>
            <button
              type="submit"
              className="w-full bg-advent-primary hover:bg-advent-primary-dark text-white py-2 px-4 rounded-lg font-semibold transition-all duration-200"
            >
              Logg inn
            </button>
          </form>
        </div>

        <HighScore />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-advent-primary-dark via-advent-primary to-advent-secondary-dark py-8 px-4">
      <div className="max-w-6xl mx-auto relative">
        {/* Logout button - absolute positioned top right */}
        <button
          onClick={handleLogout}
          className="absolute top-2 right-0 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-semibold transition-all text-sm"
        >
          Logg ut
        </button>

        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-2">
            🎄 Mobilitets adventskalender 🎄
          </h1>
          <p className="text-xl text-white">
            Finn steder i Norge hver dag fram til julaften!
          </p>
          <p className="text-lg text-yellow-300 mt-2">
            Velkommen, {user.name}!
          </p>
          {userProgress && (
            <p className="text-2xl font-bold text-white mt-2">
              Total poengsum: {userProgress.totalScore || 0}
            </p>
          )}
        </div>

        <div className="grid grid-cols-4 md:grid-cols-6 gap-4 mb-8">
          {[...Array(24)].map((_, index) => {
            const day = index + 1;
            const challenge = challenges.find((c) => c.day === day);
            const unlocked = isDayUnlocked(challenge);
            const completed = isDayCompleted(day);
            const score = getDayScore(day);

            return (
              <div
                key={day}
                onClick={() => {
                  if (unlocked && challenge) {
                    onDaySelect(challenge);
                  }
                }}
                className={`
                  relative aspect-square rounded-lg shadow-lg transition-all duration-300
                  ${
                    unlocked
                      ? completed
                        ? "bg-advent-secondary hover:bg-advent-secondary-light cursor-pointer transform hover:scale-105"
                        : "bg-white hover:bg-gray-100 cursor-pointer transform hover:scale-105"
                      : "bg-gray-400 cursor-not-allowed opacity-50"
                  }
                  flex flex-col items-center justify-center p-2
                `}
              >
                <div className="text-3xl font-bold text-gray-800">{day}</div>
                {completed && (
                  <div className="absolute top-1 right-1">
                    <span className="text-xl">✓</span>
                  </div>
                )}
                {completed && (
                  <div className="text-xs text-gray-700 font-semibold mt-1">
                    {score} pts
                  </div>
                )}
                {!unlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl">🔒</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 text-center mb-8">
          <h2 className="text-2xl font-bold mb-2 text-gray-800">
            Hvordan spille
          </h2>
          <p className="text-gray-700">
            Hver dag åpnes en ny luke med et sted i Norge. Plasser en markør så
            nært som mulig på det lite detaljerte kartet. Jo nærmere du er, desto flere poeng får
            du! Du kan bare prøve én gang per dag. Punktet for stedene er hentet fra kartverket sitt stedsnavnregister.
          </p>
        </div>

        <EasterEgg />

        {/* Leaderboard */}
        <HighScore currentUserId={user?.uid} />
      </div>
    </div>
  );
};

export default AdventCalendar;
